
import { useCallback } from "react";
import { BlogPost } from "@/types/blog";

interface UseBlogMetadataProps {
  posts: BlogPost[];
  selectedCategory: string;
  searchTerm: string;
}

export const useBlogMetadata = ({
  posts,
  selectedCategory,
  searchTerm
}: UseBlogMetadataProps) => {
  // Generate page metadata based on current filters
  const getPageMetadata = useCallback(() => {
    const pageTitle = selectedCategory 
      ? `Blog - Articoli sulla categoria ${selectedCategory} - Gildy` 
      : (searchTerm ? `Ricerca articoli: ${searchTerm} - Gildy` : "Blog - Notizie e Guide sui Metalli Preziosi - Gildy");

    const pageDescription = selectedCategory 
      ? `Esplora i nostri articoli sulla categoria ${selectedCategory}. Guide, consigli e approfondimenti sul mondo dell'oro e dei metalli preziosi.` 
      : "Approfondimenti, guide e consigli sul mondo dell'oro e dei metalli preziosi. Scopri le ultime notizie dal blog di Gildy.";

    const canonicalPath = selectedCategory 
      ? `/blog/categoria/${selectedCategory}` 
      : (searchTerm ? `/blog/search?q=${encodeURIComponent(searchTerm)}` : "/blog");

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Gildy Blog",
      "description": "Approfondimenti, guide e consigli sul mondo dell'oro e dei metalli preziosi",
      "url": "https://www.gildy.it/blog",
      "publisher": {
        "@type": "Organization",
        "name": "Gildy",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.gildy.it/logo.png"
        }
      },
      "blogPosts": posts.map(post => ({
        "@type": "BlogPosting",
        "headline": post.title,
        "image": post.featuredImage,
        "datePublished": post.date,
        "dateModified": post.modified || post.date,
        "author": {
          "@type": "Person",
          "name": post.author?.name || "Gildy Staff"  // Use optional chaining and provide default
        }
      }))
    };

    return { pageTitle, pageDescription, canonicalPath, structuredData };
  }, [selectedCategory, searchTerm, posts]);

  return { getPageMetadata };
};
